import { useEffect, useState } from "react";
import { PushPin, Trash, Plus } from "@phosphor-icons/react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { fmtAUD, MONTH_NAMES } from "../lib/format";
import { toast } from "sonner";

export default function Overrides() {
  const [routes, setRoutes] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [form, setForm] = useState({
    route_id: "", airline_iata: "SA", travel_year: new Date().getFullYear(),
    travel_month: new Date().getMonth() + 1, cabin_class: "Y", fare: "", source_note: "",
  });

  const load = async () => {
    const r1 = await api.get("/routes");
    setRoutes(r1.data);
    const r2 = await api.get("/overrides");
    setOverrides(r2.data);
    if (!form.route_id && r1.data.length) {
      setForm((f) => ({ ...f, route_id: r1.data[0].id }));
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.fare) { toast.error("Fare required"); return; }
    try {
      await api.post("/overrides", { ...form, fare: parseFloat(form.fare) });
      toast.success("Override saved");
      load();
      setForm((f) => ({ ...f, fare: "", source_note: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/overrides/${id}`);
    toast.success("Override removed");
    load();
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px]">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--saa-gold)" }}>
          <PushPin size={14} weight="duotone" /> Pinned fares
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2" data-testid="ov-title">Manual Overrides</h1>
        <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
          Pinned values override fetched fares across refreshes.
        </p>
      </div>

      {/* New override form */}
      <div className="saa-card p-5 mb-6" data-testid="ov-form">
        <div className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: "var(--text-muted)" }}>
          New override
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-7 gap-3">
          <div className="lg:col-span-2">
            <label className="text-[10px] uppercase">Route</label>
            <Select value={form.route_id} onValueChange={(v) => setForm({ ...form, route_id: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {routes.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.origin}-{r.destination}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase">Airline</label>
            <Input value={form.airline_iata} onChange={(e) => setForm({ ...form, airline_iata: e.target.value.toUpperCase() })} maxLength={2} />
          </div>
          <div>
            <label className="text-[10px] uppercase">Year</label>
            <Input type="number" value={form.travel_year} onChange={(e) => setForm({ ...form, travel_year: parseInt(e.target.value) })} />
          </div>
          <div>
            <label className="text-[10px] uppercase">Month</label>
            <Select value={`${form.travel_month}`} onValueChange={(v) => setForm({ ...form, travel_month: parseInt(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTH_NAMES.map((m, i) => <SelectItem key={i} value={`${i + 1}`}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase">Cabin</label>
            <Select value={form.cabin_class} onValueChange={(v) => setForm({ ...form, cabin_class: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Y">Y</SelectItem>
                <SelectItem value="PY">PY</SelectItem>
                <SelectItem value="C">C</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase">Fare (AUD)</label>
            <Input type="number" value={form.fare} onChange={(e) => setForm({ ...form, fare: e.target.value })} placeholder="e.g. 1899" />
          </div>
        </div>
        <Textarea
          className="mt-3"
          placeholder="Source note (e.g. quoted by SAA on Tuesday)"
          value={form.source_note}
          onChange={(e) => setForm({ ...form, source_note: e.target.value })}
          rows={2}
        />
        <div className="flex justify-end mt-3">
          <Button onClick={create} data-testid="ov-create-btn" style={{ background: "var(--saa-gold)", color: "#0A1428" }}>
            <Plus size={14} className="mr-1" /> Pin override
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="saa-card p-5">
        <div className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: "var(--text-muted)" }}>
          {overrides.length} active override{overrides.length !== 1 ? "s" : ""}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ color: "var(--text-secondary)" }} className="text-[11px] uppercase tracking-[0.12em]">
              <th className="text-left pb-2">Route</th>
              <th className="text-left pb-2">Airline</th>
              <th className="text-left pb-2">Period</th>
              <th className="text-left pb-2">Cabin</th>
              <th className="text-left pb-2">Fare</th>
              <th className="text-left pb-2">Note</th>
              <th className="text-left pb-2">By</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {overrides.map((o) => (
              <tr key={o.id} style={{ borderTop: "1px solid var(--border-subtle)" }} data-testid={`ov-row-${o.id}`}>
                <td className="py-2 font-mono">{o.route_id}</td>
                <td className="py-2 font-mono font-semibold">{o.airline_iata}</td>
                <td className="py-2">{MONTH_NAMES[o.travel_month - 1]} {o.travel_year}</td>
                <td className="py-2">{o.cabin_class}</td>
                <td className="py-2 font-mono" style={{ color: "var(--saa-gold)" }}>{fmtAUD(o.fare)}</td>
                <td className="py-2 text-xs" style={{ color: "var(--text-secondary)" }}>{o.source_note || "—"}</td>
                <td className="py-2 text-xs" style={{ color: "var(--text-secondary)" }}>{o.created_by}</td>
                <td className="py-2">
                  <button onClick={() => remove(o.id)} className="hover:text-red-500" style={{ color: "var(--text-muted)" }}>
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {overrides.length === 0 && (
              <tr><td colSpan={8} className="py-4 text-center" style={{ color: "var(--text-secondary)" }}>No overrides yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
